import { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Camera, Upload, CheckCircle2, AlertTriangle, Loader2, ShieldCheck,
  Plus, X, ArrowLeft, ScanLine, QrCode,
} from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import {
  capabilitiesLabels, customerDeviceTitle, deviceTypeLabel, deviceKeyLabel,
  roomFromDeviceKey, confidenceLabel, categoryLabel, controlStatusLabel,
} from '@/lib/deviceLabels';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MAX_PHOTO_BYTES = 8 * 1024 * 1024;
const ALLOWED_MIME = ['image/jpeg', 'image/png', 'image/webp'];

function PrivacyConsentBanner({ consented, onConsent, onDecline }) {
  if (consented) return null;
  return (
    <div className="border-2 border-emerald-300 bg-emerald-50 rounded-xl p-4 space-y-3"
      data-testid="customer-consent-banner">
      <div className="flex items-start gap-2">
        <ShieldCheck className="w-5 h-5 text-emerald-700 mt-0.5 flex-shrink-0" />
        <div>
          <h3 className="font-semibold text-emerald-900">
            Privacy &amp; gebruik van uw foto
          </h3>
          <ul className="text-sm text-emerald-900 mt-2 space-y-1 list-disc ml-4">
            <li>De foto wordt alleen gebruikt om het apparaat te herkennen.</li>
            <li>De originele foto wordt <strong>niet bewaard</strong>.</li>
            <li>Serienummers, barcodes, QR-codes, MAC- en IP-adressen en
              andere identificerende gegevens worden <strong>gestript</strong> vóór opslag.</li>
            <li>Er wordt niets automatisch verstuurd naar derden.</li>
            <li><strong>Automatische sturing van apparaten staat uit.</strong></li>
          </ul>
        </div>
      </div>
      <div className="flex gap-2 pt-1">
        <button onClick={onConsent}
          data-testid="customer-consent-accept"
          className="px-4 py-2 bg-emerald-600 text-white rounded-md text-sm font-medium hover:bg-emerald-700">
          Ik ga akkoord
        </button>
        <button onClick={onDecline}
          data-testid="customer-consent-decline"
          className="px-4 py-2 bg-white border border-emerald-300 text-emerald-800 rounded-md text-sm hover:bg-emerald-100">
          Niet akkoord
        </button>
      </div>
    </div>
  );
}

function ResultCard({ result, status, errorMsg, onAdd, added, onReset }) {
  if (status === 'idle') return null;

  if (status === 'analyzing') {
    return (
      <div className="border border-blue-200 bg-blue-50 rounded-xl p-4 flex items-center gap-2"
        data-testid="customer-photo-analyzing">
        <Loader2 className="w-5 h-5 animate-spin" />
        <span className="text-sm">Powersure analyseert uw foto... (foto wordt niet opgeslagen)</span>
      </div>
    );
  }

  if (status.startsWith('error_')) {
    const titles = {
      error_unsupported: 'Bestandstype niet ondersteund',
      error_too_large: 'Bestand te groot',
      error_consent: 'Privacy-akkoord vereist',
      error_vision_unavailable: 'Herkenning niet beschikbaar',
      error_other: 'Er ging iets mis',
    };
    return (
      <div className="border border-red-200 bg-red-50 rounded-xl p-4 flex items-start gap-2"
        data-testid={`customer-photo-${status}`}>
        <AlertTriangle className="w-5 h-5 text-red-700 mt-0.5 flex-shrink-0" />
        <div className="flex-1">
          <div className="font-semibold text-red-900">{titles[status] || 'Fout'}</div>
          <div className="text-sm text-red-800 mt-1">{errorMsg}</div>
        </div>
        <button onClick={onReset} className="text-red-700"
          data-testid="customer-photo-error-reset">
          <X className="w-4 h-4" />
        </button>
      </div>
    );
  }

  // recognized / pending_review
  const r = result || {};
  const ve = r.vision_extracted || {};
  const isPending = status === 'pending_review';
  const tone = isPending ? 'border-amber-300 bg-amber-50'
                         : 'border-emerald-300 bg-emerald-50';
  const friendlyTitle = customerDeviceTitle({
    brand: r.brand, deviceType: ve.device_type, deviceKey: r.device_key,
  });
  const friendlyType = deviceTypeLabel(ve.device_type) || deviceKeyLabel(r.device_key);
  const room = roomFromDeviceKey(r.device_key);
  const conf = confidenceLabel(r.confidence);
  const caps = capabilitiesLabels(ve.possible_capabilities);
  const certaintyTone = isPending ? 'bg-amber-100 text-amber-900 border-amber-300'
                                  : 'bg-emerald-100 text-emerald-900 border-emerald-300';

  return (
    <div className={`border-2 rounded-xl p-4 space-y-3 ${tone}`}
      data-testid={`customer-photo-${status}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h3 className="font-semibold text-lg truncate" data-testid="customer-result-title">
            {friendlyTitle}
          </h3>
          <div className="text-xs text-slate-500 mt-0.5">
            {isPending ? 'Controleer of dit klopt' : 'Apparaat herkend door Powersure'}
          </div>
        </div>
        <span className={`text-[11px] px-2 py-1 rounded-full border whitespace-nowrap ${certaintyTone}`}
          data-testid="customer-r-certainty">
          {isPending ? 'Controle nodig' : conf.label}
        </span>
      </div>

      {isPending && (
        <div className="bg-amber-100 border border-amber-300 rounded-lg p-2 text-sm text-amber-900"
          data-testid="customer-photo-pending-banner">
          <AlertTriangle className="w-4 h-4 inline mr-1" />
          We weten het niet helemaal zeker. Controleer hieronder of dit klopt vóór u het toevoegt.
        </div>
      )}

      <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-sm">
        <div><span className="text-slate-500">Merk:</span> <span data-testid="customer-r-brand">{r.brand || '—'}</span></div>
        <div><span className="text-slate-500">Model:</span> <span data-testid="customer-r-model">{r.model || '—'}</span></div>
        {friendlyType && (
          <div><span className="text-slate-500">Type:</span> <span data-testid="customer-r-device-type">{friendlyType}</span></div>
        )}
        {room && (
          <div><span className="text-slate-500">Ruimte:</span> <span data-testid="customer-r-room">{room}</span></div>
        )}
        {caps.length > 0 && (
          <div className="col-span-2">
            <span className="text-slate-500">Mogelijke functies:</span>{' '}
            <span data-testid="customer-r-capabilities">
              {caps.join(', ')}
            </span>
          </div>
        )}
      </div>

      <div className="bg-white/70 border border-emerald-200 rounded-lg p-2.5 text-xs flex items-start gap-2"
        data-testid="customer-photo-control-notice">
        <ShieldCheck className="w-4 h-4 mt-0.5 text-emerald-700 flex-shrink-0" />
        <span><strong>Automatische sturing staat uit.</strong> Powersure schakelt nu nog niets.</span>
      </div>

      <div className="bg-white/70 border border-emerald-200 rounded-lg p-2.5 text-xs flex items-start gap-2"
        data-testid="customer-photo-pii-notice">
        <ShieldCheck className="w-4 h-4 mt-0.5 text-emerald-700 flex-shrink-0" />
        <span>
          Foto wordt niet bewaard. Serienummers en netwerkgegevens worden automatisch verwijderd.
        </span>
      </div>

      <div className="flex gap-2 pt-1">
        {added ? (
          <span className="text-emerald-700 flex items-center gap-1 text-sm font-medium"
            data-testid="customer-photo-added">
            <CheckCircle2 className="w-5 h-5" /> Apparaat toegevoegd
          </span>
        ) : (
          <button onClick={() => onAdd(r)}
            data-testid="customer-photo-add"
            className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-md text-sm font-medium flex items-center gap-1">
            <Plus className="w-4 h-4" /> Toevoegen aan Powersure
          </button>
        )}
        <button onClick={onReset}
          data-testid="customer-photo-new"
          className="px-4 py-2 bg-white border border-slate-200 rounded-md text-sm">
          Nieuwe foto
        </button>
      </div>
    </div>
  );
}

function PostAddInfo() {
  return (
    <div className="space-y-3" data-testid="customer-post-add-info">
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm space-y-1">
        <p>✓ Powersure gebruikt dit apparaat voor beter inzicht en advies.</p>
        <p>✓ Automatische sturing staat <strong>uit</strong>.</p>
        <p>✓ Eventuele sturing kan later alleen met aparte toestemming.</p>
      </div>
      <div className="bg-white border border-emerald-200 rounded-lg p-3 text-sm space-y-1"
        data-testid="customer-value-card">
        <div className="font-semibold text-emerald-900 mb-1">
          Wat Powersure nu met dit apparaat kan
        </div>
        <ul className="list-disc ml-5 text-slate-700 space-y-0.5">
          <li>Meenemen in uw energieprofiel</li>
          <li>Inschatten of het apparaat energie-relevant is</li>
          <li>Advies geven over meten en eventueel sturen later</li>
          <li>Aangeven of een installateur nodig is</li>
          <li>Automatische sturing staat uit</li>
        </ul>
      </div>
    </div>
  );
}

/**
 * BarcodeScannerCard — uses the browser BarcodeDetector API to scan QR /
 * EAN / UPC codes from the device's camera. Feature-detected: when the
 * browser does not support it (Safari iOS today, older Firefox), we render
 * a short notice and let the user fall back to the photo-upload card below.
 *
 * Privacy:
 *  - The video stream is processed entirely in-browser. Frames are NEVER
 *    sent to the server. Only the decoded code string + format is POSTed
 *    to /barcode for LLM interpretation, and the backend never persists it.
 */
function BarcodeScannerCard({
  consented, scanning, onStart, onStop, scanError, supportChecked, supported,
  videoRef,
}) {
  if (!consented) return null;

  if (supportChecked && !supported) {
    return (
      <div className="bg-white border border-border rounded-xl p-4 space-y-2"
        data-testid="customer-barcode-unsupported">
        <div className="flex items-center gap-2 text-sm">
          <QrCode className="w-4 h-4 text-slate-500" />
          <span className="font-medium">Streepjescode of QR-code scannen</span>
        </div>
        <p className="text-xs text-muted-foreground">
          Uw browser ondersteunt scannen niet rechtstreeks. Gebruik
          hieronder <strong>&quot;Maak foto&quot;</strong> of <strong>&quot;Foto kiezen&quot;</strong>{' '}
          — Powersure herkent het apparaat ook via een foto van het label of typeplaatje.
        </p>
      </div>
    );
  }

  return (
    <div className="bg-white border border-border rounded-xl p-4 space-y-3"
      data-testid="customer-barcode-card">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <ScanLine className="w-4 h-4 text-emerald-700" />
          <span className="font-medium text-sm">Streepjescode of QR-code scannen</span>
        </div>
        {scanning ? (
          <button onClick={onStop}
            data-testid="customer-barcode-stop"
            className="px-3 py-1.5 text-xs bg-white border border-border rounded-md inline-flex items-center gap-1">
            <X className="w-3.5 h-3.5" /> Stop
          </button>
        ) : (
          <button onClick={onStart}
            data-testid="customer-barcode-start"
            className="px-3 py-1.5 text-xs bg-emerald-600 text-white rounded-md inline-flex items-center gap-1 hover:bg-emerald-700">
            <ScanLine className="w-3.5 h-3.5" /> Start scan
          </button>
        )}
      </div>
      {scanning && (
        <div className="relative rounded-lg overflow-hidden border border-emerald-200 bg-black"
          data-testid="customer-barcode-viewport">
          <video ref={videoRef}
            data-testid="customer-barcode-video"
            playsInline muted
            className="w-full max-h-72 object-cover" />
          <div className="absolute inset-0 pointer-events-none flex items-center justify-center">
            <div className="w-3/4 h-1/3 border-2 border-emerald-400/80 rounded-md
                            shadow-[0_0_0_9999px_rgba(0,0,0,0.35)]" />
          </div>
        </div>
      )}
      <p className="text-xs text-muted-foreground">
        Houd de QR- of streepjescode op het typeplaatje stil binnen het kader.
        Het beeld wordt niet opgeslagen.
      </p>
      {scanError && (
        <div className="text-xs text-red-700 bg-red-50 border border-red-200 rounded-md p-2"
          data-testid="customer-barcode-error">
          {scanError}
        </div>
      )}
    </div>
  );
}

export default function CustomerDeviceOnboardingPage() {
  const navigate = useNavigate();
  const [token, setToken] = useState(null);
  const [user, setUser] = useState(null);
  const [authChecked, setAuthChecked] = useState(false);
  const [consented, setConsented] = useState(false);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [status, setStatus] = useState('idle');
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [added, setAdded] = useState(false);
  const [profiles, setProfiles] = useState([]);
  const [hadPilotToken, setHadPilotToken] = useState(false);
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);

  // BarcodeDetector scanner state
  const [scanSupportChecked, setScanSupportChecked] = useState(false);
  const [scanSupported, setScanSupported] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [scanError, setScanError] = useState('');
  const videoRef = useRef(null);
  const detectorRef = useRef(null);
  const streamRef = useRef(null);
  const rafRef = useRef(null);
  const scanBusyRef = useRef(false);

  useEffect(() => {
    // Feature-detect BarcodeDetector. Available on Chrome (desktop+Android)
    // and Edge. Not yet on Safari iOS / Firefox — those users fall back to
    // the photo-upload path automatically.
    if (typeof window === 'undefined' || !('BarcodeDetector' in window)) {
      setScanSupported(false);
      setScanSupportChecked(true);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const formats = await window.BarcodeDetector.getSupportedFormats();
        if (cancelled) return;
        const wanted = ['qr_code', 'ean_13', 'ean_8', 'upc_a', 'upc_e',
                        'code_128', 'code_39', 'codabar', 'data_matrix'];
        const supportedFormats = formats.filter((f) => wanted.includes(f));
        if (supportedFormats.length === 0) {
          setScanSupported(false);
        } else {
          detectorRef.current = new window.BarcodeDetector({
            formats: supportedFormats,
          });
          setScanSupported(true);
        }
      } catch {
        setScanSupported(false);
      } finally {
        setScanSupportChecked(true);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const stopScanner = () => {
    setScanning(false);
    scanBusyRef.current = false;
    if (rafRef.current) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    if (videoRef.current) {
      try { videoRef.current.srcObject = null; } catch { /* noop */ }
    }
  };

  const handleScannedCode = async (codeValue, codeFormat) => {
    stopScanner();
    setResult(null);
    setAdded(false);
    setStatus('analyzing');
    setError('');
    try {
      const r = await axios.post(
        `${API}/customer/device-onboarding/barcode`,
        { code_value: codeValue, code_format: codeFormat, consent_given: true },
        { headers: { Authorization: `Bearer ${token}` }, timeout: 60000 },
      );
      const res = r.data?.result || {};
      const conf = Number(res.confidence || 0);
      const isUnknown = res.device_key === 'unknown_device';
      const isPending = conf < 0.5 || isUnknown
        || r.data?.control_summary?.vision_status === 'error';
      setResult(res);
      setStatus(isPending ? 'pending_review' : 'recognized');
    } catch (err) {
      const s = err?.response?.status;
      const d = err?.response?.data?.detail;
      if (s === 503) {
        setStatus('error_vision_unavailable');
        setError('Herkenning is tijdelijk niet beschikbaar. Probeer later opnieuw.');
      } else if (s === 401 || s === 403) {
        setStatus('error_other');
        setError('Niet ingelogd. Log eerst in om door te gaan.');
      } else if (s === 400) {
        setStatus('error_other');
        setError(d?.reason || 'De scan kon niet worden verwerkt. Probeer opnieuw.');
      } else {
        setStatus('error_other');
        setError(err?.message || 'Onbekende fout.');
      }
    }
  };

  const startScanner = async () => {
    setScanError('');
    if (!consented) {
      setScanError('Stem eerst in met het privacy-beleid hierboven.');
      return;
    }
    if (!detectorRef.current) {
      setScanError('Uw browser ondersteunt scannen niet. Gebruik foto-upload hieronder.');
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: false,
        video: { facingMode: { ideal: 'environment' } },
      });
      streamRef.current = stream;
      setScanning(true);
      // Wait one tick so the <video> element renders, then attach the stream
      setTimeout(async () => {
        if (!videoRef.current) return;
        videoRef.current.srcObject = stream;
        try { await videoRef.current.play(); } catch { /* user gesture handled */ }
        const tick = async () => {
          if (!videoRef.current || !detectorRef.current
              || !streamRef.current || scanBusyRef.current) {
            rafRef.current = requestAnimationFrame(tick);
            return;
          }
          try {
            const codes = await detectorRef.current.detect(videoRef.current);
            if (codes && codes.length > 0) {
              const c = codes[0];
              scanBusyRef.current = true;
              handleScannedCode(c.rawValue || '', c.format || 'unknown');
              return;
            }
          } catch { /* transient decode errors are fine, just keep scanning */ }
          rafRef.current = requestAnimationFrame(tick);
        };
        rafRef.current = requestAnimationFrame(tick);
      }, 60);
    } catch (err) {
      stopScanner();
      const name = err?.name || '';
      if (name === 'NotAllowedError' || name === 'SecurityError') {
        setScanError('Cameratoegang geweigerd. Sta de camera toe of gebruik foto-upload hieronder.');
      } else if (name === 'NotFoundError') {
        setScanError('Geen camera gevonden. Gebruik foto-upload hieronder.');
      } else {
        setScanError('Scannen lukt niet. Gebruik foto-upload hieronder.');
      }
    }
  };

  // Stop scanner on unmount so we never leak a camera track.
  useEffect(() => () => stopScanner(), []);

  useEffect(() => {
    // Accept either a regular customer token, an admin token, or a pilot
    // magic token. Customer tokens live under `powersure_customer_token`
    // (set by /customer/auth/login). Admin tokens live under `admin_token`
    // or `access_token`. Pilot tokens live under `powersure_pilot_token`.
    const pilotTok = localStorage.getItem('powersure_pilot_token');
    const customerTok = localStorage.getItem('powersure_customer_token');
    const adminTok = localStorage.getItem('admin_token') || localStorage.getItem('access_token');
    const t = customerTok || adminTok || pilotTok;
    setToken(t);
    if (pilotTok && !customerTok && !adminTok) setHadPilotToken(true);
    if (!t) {
      setAuthChecked(true);
      return;
    }
    // Customer JWT path
    if (customerTok) {
      axios.get(`${API}/customer/auth/me`, { headers: { Authorization: `Bearer ${customerTok}` } })
        .then((r) => { setUser({ role: 'customer', ...(r.data || {}) }); setAuthChecked(true); })
        .catch(() => { localStorage.removeItem('powersure_customer_token'); setToken(null); setAuthChecked(true); });
      return;
    }
    // Pilot identity path
    if (!adminTok && pilotTok) {
      axios.get(`${API}/pilot-devices/me`, { headers: { Authorization: `Bearer ${pilotTok}` } })
        .then((r) => {
          const p = r.data?.pilot;
          if (p) setUser({ role: 'pilot', name: p.name, pilot_id: p.pilot_id, pilot_type: p.pilot_type });
        })
        .catch(() => { localStorage.removeItem('powersure_pilot_token'); setToken(null); })
        .finally(() => setAuthChecked(true));
      return;
    }
    // Admin path
    axios.get(`${API}/auth/me`, { headers: { Authorization: `Bearer ${t}` } })
      .then((r) => { setUser(r.data); setAuthChecked(true); })
      .catch(() => { setAuthChecked(true); });
  }, []);

  useEffect(() => {
    if (!token || !user) return;
    axios.get(`${API}/customer/device-onboarding/profiles`,
      { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => setProfiles(r.data?.items || []))
      .catch(() => {});
  }, [token, user, added]);

  const resetPhoto = () => {
    setStatus('idle');
    setResult(null);
    setError('');
    setPhotoPreview(null);
    setAdded(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
    if (cameraInputRef.current) cameraInputRef.current.value = '';
  };

  const handleFile = (file) => {
    if (!file) return;
    setResult(null); setAdded(false);
    if (!consented) {
      setStatus('error_consent');
      setError('Stem eerst in met het privacy-beleid hierboven.');
      return;
    }
    if (!ALLOWED_MIME.includes(file.type)) {
      setStatus('error_unsupported');
      setError(`Type '${file.type || 'onbekend'}' wordt niet ondersteund. Gebruik JPEG, PNG of WebP.`);
      return;
    }
    if (file.size > MAX_PHOTO_BYTES) {
      const mb = (file.size / 1024 / 1024).toFixed(2);
      setStatus('error_too_large');
      setError(`Bestand is ${mb} MB, maximaal 8 MB toegestaan.`);
      return;
    }
    const reader = new FileReader();
    reader.onload = (e) => setPhotoPreview(e.target?.result || null);
    reader.readAsDataURL(file);
    uploadFile(file);
  };

  const uploadFile = async (file) => {
    setStatus('analyzing');
    setError('');
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('consent_given', 'true');
      const r = await axios.post(
        `${API}/customer/device-onboarding/photo`, fd,
        { headers: { Authorization: `Bearer ${token}`,
                     'Content-Type': 'multipart/form-data' },
          timeout: 60000 },
      );
      const res = r.data?.result || {};
      const conf = Number(res.confidence || 0);
      const isUnknown = res.device_key === 'unknown_device';
      const isPending = conf < 0.5 || isUnknown
        || r.data?.control_summary?.vision_status === 'error';
      setResult(res);
      setStatus(isPending ? 'pending_review' : 'recognized');
    } catch (err) {
      const s = err?.response?.status;
      const d = err?.response?.data?.detail;
      if (s === 400 && d?.error === 'unsupported_mime') {
        setStatus('error_unsupported');
        setError(`Server weigerde type ${d.received || ''}. Gebruik JPEG/PNG/WebP.`);
      } else if (s === 400 && d?.error === 'file_too_large') {
        setStatus('error_too_large');
        setError(`Server limiet: max ${Math.round((d.max_bytes || 0) / 1024 / 1024)} MB.`);
      } else if (s === 400 && d?.error === 'consent_required') {
        setStatus('error_consent');
        setError(d.reason || 'Privacy-akkoord vereist.');
      } else if (s === 503) {
        setStatus('error_vision_unavailable');
        setError('Herkenning is tijdelijk niet beschikbaar. Probeer later opnieuw.');
      } else if (s === 401 || s === 403) {
        setStatus('error_other');
        setError('Niet ingelogd. Log eerst in om door te gaan.');
      } else {
        setStatus('error_other');
        setError(err?.message || 'Onbekende fout.');
      }
    }
  };

  const addDevice = async (r) => {
    try {
      await axios.post(
        `${API}/customer/device-onboarding/add`,
        { device_key: r.device_key, brand: r.brand, model: r.model,
          confidence: r.confidence, consent_given: true },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setAdded(true);
      toast.success(`${r.brand || ''} ${r.model || r.device_key} toegevoegd`);
    } catch (e) {
      toast.error(e?.response?.data?.detail?.reason || e?.response?.data?.detail || e.message);
    }
  };

  if (!authChecked) {
    return <div className="min-h-screen flex items-center justify-center"
      data-testid="customer-loading">
      <Loader2 className="w-6 h-6 animate-spin" />
    </div>;
  }

  if (!token || !user) {
    // hadPilotToken is captured at mount BEFORE any .catch removes the key,
    // so we can render the customer-friendly "Pilotlink verlopen" copy even
    // after the bad token has been cleared from storage.
    return (
      <div className="min-h-screen flex items-center justify-center p-4 bg-slate-50">
        <div className="max-w-md text-center space-y-4" data-testid="customer-need-login">
          <h1 className="text-2xl font-semibold">
            {hadPilotToken ? 'Pilotlink verlopen' : 'Inloggen vereist'}
          </h1>
          <p className="text-muted-foreground">
            {hadPilotToken
              ? 'Deze pilotlink is verlopen of ongeldig. Vraag uw contactpersoon bij Powersure om een nieuwe link.'
              : 'Apparaat toevoegen kan binnen Mijn Powersure of via een Powersure-account.'}
          </p>
          <button onClick={() => navigate('/')}
            data-testid="customer-need-login-home"
            className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-md">
            Naar homepage
          </button>
        </div>
      </div>
    );
  }

  const isPilot = user?.role === 'pilot';
  const backHref = isPilot ? '/app' : '/';

  return (
    <div className="min-h-screen bg-slate-50" data-testid="customer-onboarding-page">
      <div className="max-w-2xl mx-auto px-4 py-6 space-y-5">
        <Link to={backHref} className="text-sm text-muted-foreground inline-flex items-center gap-1"
          data-testid="customer-onboarding-back">
          <ArrowLeft className="w-4 h-4" /> Terug{isPilot ? ' naar Mijn Powersure' : ''}
        </Link>

        <div>
          <h1 className="text-3xl font-bold tracking-tight" data-testid="customer-onboarding-title">
            Voeg uw apparaat toe
          </h1>
          <p className="text-muted-foreground mt-1">
            Maak een foto van het typeplaatje of upload een foto. Powersure
            probeert merk, model en apparaattype te herkennen.
          </p>
          <p className="text-xs text-muted-foreground mt-1">
            Ingelogd als <strong>{isPilot ? `${user.name} \u00b7 pilot` : user.email}</strong>
          </p>
        </div>

        <PrivacyConsentBanner
          consented={consented}
          onConsent={() => setConsented(true)}
          onDecline={() => navigate('/')}
        />

        <BarcodeScannerCard
          consented={consented}
          scanning={scanning}
          onStart={startScanner}
          onStop={stopScanner}
          scanError={scanError}
          supportChecked={scanSupportChecked}
          supported={scanSupported}
          videoRef={videoRef}
        />

        {consented && (
          <div className="bg-white border border-border rounded-xl p-4 space-y-3"
            data-testid="customer-upload-card">
            <div className="flex flex-wrap gap-2">
              <button onClick={() => fileInputRef.current?.click()}
                data-testid="customer-upload-btn"
                disabled={status === 'analyzing'}
                className="px-4 py-2 bg-primary text-white rounded-md flex items-center gap-2 disabled:opacity-50">
                <Upload className="w-4 h-4" /> Foto kiezen
              </button>
              <button onClick={() => cameraInputRef.current?.click()}
                data-testid="customer-camera-btn"
                disabled={status === 'analyzing'}
                className="px-4 py-2 bg-white border border-border rounded-md flex items-center gap-2 disabled:opacity-50">
                <Camera className="w-4 h-4" /> Maak foto
              </button>
              <input ref={fileInputRef} type="file"
                accept="image/jpeg,image/png,image/webp"
                data-testid="customer-file-input"
                className="hidden"
                onChange={(e) => handleFile(e.target.files?.[0])} />
              <input ref={cameraInputRef} type="file"
                accept="image/jpeg,image/png,image/webp"
                capture="environment"
                data-testid="customer-camera-input"
                className="hidden"
                onChange={(e) => handleFile(e.target.files?.[0])} />
            </div>
            <p className="text-xs text-muted-foreground">
              JPEG · PNG · WebP · max 8 MB
            </p>
            {photoPreview && (
              <img src={photoPreview} alt="preview"
                data-testid="customer-photo-preview"
                className="max-h-40 rounded-md border border-border" />
            )}
          </div>
        )}

        <ResultCard
          result={result} status={status} errorMsg={error}
          onAdd={addDevice} added={added} onReset={resetPhoto} />

        {added && <PostAddInfo />}

        {profiles.length > 0 && (
          <div className="bg-white border border-slate-200 rounded-xl p-4"
            data-testid="customer-profiles-card">
            <h2 className="font-semibold mb-3">Uw toegevoegde apparaten ({profiles.length})</h2>
            <div className="space-y-2">
              {profiles.map((p) => {
                const subtitle = [categoryLabel(p.category), roomFromDeviceKey(p.device_key)]
                  .filter(Boolean).join(' · ');
                const statusLabel = p.pending_review ? 'Controle nodig'
                                    : (controlStatusLabel(p.control_status) || 'Herkend');
                const statusTone = p.pending_review
                  ? 'bg-amber-100 text-amber-800 border-amber-200'
                  : 'bg-emerald-100 text-emerald-800 border-emerald-200';
                return (
                  <div key={p.profile_id}
                    data-testid={`customer-profile-${p.profile_id}`}
                    className="border-b border-slate-200/60 pb-2 last:border-b-0 last:pb-0">
                    <div className="flex justify-between items-center gap-3">
                      <div className="min-w-0">
                        <div className="font-medium text-sm truncate">{p.brand || 'Onbekend merk'} {p.model || ''}</div>
                        <div className="text-xs text-slate-500">{subtitle || '—'}</div>
                      </div>
                      <span className={`text-[10px] px-2 py-0.5 rounded-full border whitespace-nowrap ${statusTone}`}>
                        {statusLabel}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
